import { MessageSquare } from "lucide-react";

import { Badge } from "@/components/ui/badge";

interface TopicEntry {
  topic: string;
  timestamp: string;
  firstMentioned: string;
}

interface TopicsSectionProps {
  topicEntries: TopicEntry[];
  meetingStartTime?: Date | null;
}

const TopicsSection = ({
  topicEntries,
  meetingStartTime,
}: TopicsSectionProps) => {
  // Format timestamp for display - show relative time from meeting start
  const formatTime = (timestamp: string) => {
    const topicTime = new Date(timestamp);

    if (meetingStartTime) {
      const diffMs = topicTime.getTime() - meetingStartTime.getTime();
      const diffMinutes = Math.floor(diffMs / (1000 * 60));
      const diffSeconds = Math.floor((diffMs % (1000 * 60)) / 1000);

      return `${diffMinutes.toString().padStart(2, "0")}:${diffSeconds.toString().padStart(2, "0")}`;
    }

    // Fallback to showing the actual time
    return topicTime.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <>
      <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-gray-200">
        <MessageSquare className="size-4" />
        Topics Mentioned
      </h3>
      <div className="max-h-64 space-y-2 overflow-y-auto pr-2">
        {topicEntries.length === 0 ? (
          <div className="flex items-center justify-center rounded-lg border border-gray-600/30 bg-gray-800/60 p-4 text-sm text-gray-400">
            No topics mentioned yet.
          </div>
        ) : (
          topicEntries.map((topicEntry, index) => (
            <div
              key={`${topicEntry.topic}-${topicEntry.firstMentioned}`}
              className="rounded-lg border border-blue-500/30 bg-blue-600/20 p-2 transition-all duration-300"
            >
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-blue-300">
                  {topicEntry.topic}
                </span>
                <Badge
                  variant="secondary"
                  className="bg-blue-600 text-xs text-white"
                >
                  {formatTime(topicEntry.firstMentioned)}
                </Badge>
              </div>
            </div>
          ))
        )}
      </div>
    </>
  );
};

export default TopicsSection;
